import {useDebugValue} from 'react';

import {useSystemOptions} from './useSystemOptions';

import {useMediaActions} from './useMediaActions';
import {useMediaEvents} from './useMediaEvents';
import {useMediaOptions} from './useMediaOptions';
import {useMediaSettings} from './useMediaSettings';

import {useAbacusStore} from './useAbacusStore';

import {useTranslationsOverrides} from './useTranslationsOverrides';
import {useDefaultTranslations} from './useDefaultTranslations';

import {useEdapOptions} from './useEdapOptions';

import {useSearchActions} from './useSearchActions';
import {useSearchCookies} from './useSearchCookies';
import {useSearchEvents} from './useSearchEvents';
import {useSearchValues} from './useSearchValues';

import {useFormActions} from './useFormActions';
import {useFormEvents} from './useFormEvents';
import {useFormOptions} from './useFormOptions';
import {useFormSettings} from './useFormSettings';

import {useDestinationActions} from './useDestinationActions';
import {useDestinationEvents} from './useDestinationEvents';
import {useDestinationOptions} from './useDestinationOptions';
import {useDestinationSettings} from './useDestinationSettings';
import {useDestinationValues} from './useDestinationValues';

import {useDestinationSummaryEvents} from './useDestinationSummaryEvents';

import {useDatesActions} from './useDatesActions';
import {useDatesEvents} from './useDatesEvents';
import {useDatesOptions} from './useDatesOptions';
import {useDatesSettings} from './useDatesSettings';
import {useDatesValues} from './useDatesValues';

import {useGuestsActions} from './useGuestsActions';
import {useGuestsEvents} from './useGuestsEvents';
import {useGuestsOptions} from './useGuestsOptions';
import {useGuestsSettings} from './useGuestsSettings';
import {useGuestsValues} from './useGuestsValues';

import {useSubmitEvents} from './useSubmitEvents';
import {useSubmitOptions} from './useSubmitOptions';

import {useSubmitSummaryEvents} from './useSubmitSummaryEvents';

import {useSubmitDetailsEvents} from './useSubmitDetailsEvents';

import {useDetailsModalActions} from './useDetailsModalActions';
import {useDetailsModalEvents} from './useDetailsModalEvents';
import {useDetailsModalOptions} from './useDetailsModalOptions';
import {useDetailsModalSettings} from './useDetailsModalSettings';

function useSearchFormContext(props) {
    const {
        systemOptions
    } = useSystemOptions(props);

    const {
        mediaEvents
    } = useMediaEvents(props);

    const {
        mediaOptions
    } = useMediaOptions(props);

    const {
        initialMediaSettings,
        mediaSettings,
        changeMediaSettings
    } = useMediaSettings(props);

    useMediaActions({
        mediaEvents,
        mediaOptions,
        initialMediaSettings,
        changeMediaSettings
    });

    const {
        abacusStore
    } = useAbacusStore(props);

    const {
        edapOptions
    } = useEdapOptions(props);

    const {
        searchCookies,
        setSearchCookies
    } = useSearchCookies();

    const {
        searchEvents
    } = useSearchEvents(props);

    const {
        initialSearchValues,
        currentSearchValues,
        latestSearchValues,
        setAllSearchValuesInitial
    } = useSearchValues(props);

    useSearchActions({
        searchEvents,
        initialSearchValues,
        currentSearchValues,
        latestSearchValues,
        setAllSearchValuesInitial
    });

    const {
        formEvents
    } = useFormEvents(props);

    const {
        formOptions
    } = useFormOptions(props);

    const {
        initialFormSettings,
        formSettings,
        changeFormSettings
    } = useFormSettings(props);

    const formActions = useFormActions({
        formEvents,
        initialFormSettings,
        changeFormSettings
    });

    const {
        destinationEvents
    } = useDestinationEvents(props);

    const {
        destinationOptions
    } = useDestinationOptions(props, abacusStore, formSettings, systemOptions);

    const {
        initialDestinationSettings,
        destinationSettings,
        changeDestinationSettings
    } = useDestinationSettings(props);


    const {
        initialDestinationValues,
        currentDestinationValues,
        latestDestinationValues,
        setAllDestinationValues,
        setAllDestinationValuesInitial
    } = useDestinationValues(props, searchCookies, systemOptions, abacusStore);

    const destinationActions = useDestinationActions({
        destinationEvents,
        initialDestinationSettings,
        changeDestinationSettings,
        initialDestinationValues,
        currentDestinationValues,
        latestDestinationValues,
        setAllDestinationValues,
        setAllDestinationValuesInitial
    });

    const {
        destinationSummaryEvents
    } = useDestinationSummaryEvents(props);

    const {
        datesEvents
    } = useDatesEvents(props);

    const {
        datesOptions
    } = useDatesOptions(props, abacusStore);

    const {
        initialDatesSettings,
        datesSettings,
        changeDatesSettings
    } = useDatesSettings(props);

    const {
        initialDatesValues,
        currentDatesValues,
        latestDatesValues,
        setCurrentDatesValuesDefault,
        setCurrentDatesValuesLatest,
        setLatestDatesValues,
        setAllDatesValuesDefault,
        setAllDatesValuesInitial
    } = useDatesValues(props, searchCookies, systemOptions, abacusStore);

    const datesActions = useDatesActions({
        datesEvents,
        initialDatesSettings,
        changeDatesSettings,
        initialDatesValues,
        currentDatesValues,
        latestDatesValues,
        setCurrentDatesValuesDefault,
        setCurrentDatesValuesLatest,
        setLatestDatesValues,
        setAllDatesValuesDefault,
        setAllDatesValuesInitial,
        setSearchCookies
    });

    const {
        guestsEvents
    } = useGuestsEvents(props);

    const {
        guestsOptions
    } = useGuestsOptions(props, abacusStore);

    const {
        initialGuestsSettings,
        guestsSettings,
        changeGuestsSettings
    } = useGuestsSettings(props);


    const {
        initialGuestsValues,
        currentGuestsValues,
        latestGuestsValues,
        setCurrentGuestsValuesLatest,
        setLatestGuestsValues,
        setAllGuestsValuesDefault,
        setAllGuestsValuesInitial
    } = useGuestsValues(props, searchCookies, systemOptions, abacusStore);

    const guestsActions = useGuestsActions({
        guestsEvents,
        initialGuestsSettings,
        changeGuestsSettings,
        initialGuestsValues,
        currentGuestsValues,
        latestGuestsValues,
        setCurrentGuestsValuesLatest,
        setLatestGuestsValues,
        setAllGuestsValuesDefault,
        setAllGuestsValuesInitial
    });

    const {
        submitEvents
    } = useSubmitEvents(props);

    const {
        submitOptions
    } = useSubmitOptions(props);

    const {
        submitSummaryEvents
    } = useSubmitSummaryEvents(props);

    const {
        submitDetailsEvents
    } = useSubmitDetailsEvents(props);

    const {
        detailsModalEvents
    } = useDetailsModalEvents(props);

    const {
        detailsModalOptions
    } = useDetailsModalOptions(props);

    const {
        initialDetailsModalSettings,
        detailsModalSettings,
        changeDetailsModalSettings
    } = useDetailsModalSettings(props);

    const detailsModalActions = useDetailsModalActions({
        detailsModalEvents,
        initialDetailsModalSettings,
        changeDetailsModalSettings
    });

    const translations = useDefaultTranslations(props);

    const translationsOverrides = useTranslationsOverrides();

    const result = {
        systemOptions,
        mediaEvents,
        mediaOptions,
        mediaSettings,
        abacusStore,
        translations,
        translationsOverrides,
        edapOptions,
        searchEvents,
        currentSearchValues,
        latestSearchValues,
        formActions,
        formEvents,
        formOptions,
        formSettings,
        destinationActions,
        destinationEvents,
        destinationOptions,
        destinationSettings,
        currentDestinationValues,
        latestDestinationValues,
        destinationSummaryEvents,
        datesActions,
        datesEvents,
        datesOptions,
        datesSettings,
        currentDatesValues,
        latestDatesValues,
        guestsActions,
        guestsEvents,
        guestsOptions,
        guestsSettings,
        currentGuestsValues,
        latestGuestsValues,
        submitEvents,
        submitOptions,
        submitSummaryEvents,
        submitDetailsEvents,
        detailsModalActions,
        detailsModalEvents,
        detailsModalOptions,
        detailsModalSettings,
    };

    useDebugValue(result);

    return result;
}

export {useSearchFormContext};
